import { useState, useEffect } from 'react';

/**
 * usePulse for living dynamics (jitter).
 * Purely cosmetic — this does NOT fetch or reflect any real data. It takes a real `value`
 * (e.g. a poll's vote count) and periodically returns a slightly randomized number around it,
 * so displayed counters feel "alive" between real updates instead of sitting frozen. Every
 * `interval` ms it re-rolls a random offset within +/-(value * jitterConfig), re-clamped to
 * `min`. Values under 10 are never jittered (returned as-is) so small counts don't visibly
 * flicker by a large relative amount. The underlying `value` itself is the source of truth;
 * this hook never persists or reports the jittered number anywhere else.
*/
export function usePulse(value, jitterConfig = 0.05, interval = 8000, min = 0) {
    const [pulsedValue, setPulsedValue] = useState(value);

    useEffect(() => {
        setPulsedValue(value);
        const timer = setInterval(() => {
            // Stable Jitter: Small numbers (1-10) don't jump. 
            // Large numbers use a tighter 5% range for professional stability.
            if (value < 10) {
                setPulsedValue(value);
                return;
            }

            const calculatedRange = Math.floor(value * jitterConfig);
            const jitter = calculatedRange > 0
                ? Math.floor(Math.random() * (calculatedRange * 2 + 1)) - calculatedRange
                : 0;

            setPulsedValue(Math.max(min, value + jitter));
        }, interval);
        return () => clearInterval(timer);
    }, [value, jitterConfig, interval, min]);

    return pulsedValue;
}
